/**
 * /contacts/:contactId — profile + tags + custom fields editor + recent
 * messages preview. Tags can be added/removed inline; custom field
 * values are merged via PATCH (typed coercion happens server-side).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";

interface ContactDetail {
  contact: {
    id: string;
    igAccountId: string;
    igUsername: string | null;
    name: string | null;
    profilePicUrl: string | null;
    optIn: { marketing: boolean; transactional: boolean };
    customFields: Record<string, unknown>;
    lastInteractionAt: string | null;
    createdAt: string;
  };
  tags: { tagId: string; appliedAt: string; appliedBy: string }[];
  conversation: { id: string; lastMessageAt: string | null } | null;
  recentMessages: {
    id: string;
    direction: "inbound" | "outbound";
    type: string;
    body: { text?: string } | null;
    status: string;
    createdAt: string;
  }[];
  subscriptions: {
    sequenceId: string;
    sequenceName: string;
    status: string;
    currentStepIdx: number;
    nextRunAt: string | null;
    startedAt: string;
    unsubscribedAt: string | null;
    unsubscribedReason: string | null;
  }[];
}

interface TagRow {
  id: string;
  name: string;
  color: string | null;
}

interface FieldDef {
  id: string;
  name: string;
  label: string;
  type: "text" | "number" | "date" | "bool";
}

export const Route = createFileRoute("/_app/contacts/$contactId")({
  component: ContactDetailPage,
});

function ContactDetailPage() {
  const { contactId } = Route.useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<ContactDetail | null>(null);
  const [tags, setTags] = useState<TagRow[]>([]);
  const [fieldDefs, setFieldDefs] = useState<FieldDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<string>("");

  const tagById = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);
  const appliedTagIds = useMemo(
    () => new Set(data?.tags.map((t) => t.tagId) ?? []),
    [data],
  );
  const availableTags = useMemo(
    () => tags.filter((t) => !appliedTagIds.has(t.id)),
    [tags, appliedTagIds],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, t, f] = await Promise.all([
        api.get<ContactDetail>(`/contacts/${contactId}`),
        api.get<{ tags: TagRow[] }>("/tags"),
        api.get<{ fields: FieldDef[] }>("/custom-fields"),
      ]);
      setData(d.data);
      setTags(t.data.tags);
      setFieldDefs(f.data.fields);
    } catch {
      setError("Failed to load contact");
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function addTag(tagId: string) {
    if (!tagId) return;
    try {
      await api.post(`/contacts/${contactId}/tags`, { tagId });
      setAdding("");
      await load();
    } catch {
      setError("Failed to add tag");
    }
  }

  async function removeTag(tagId: string) {
    try {
      await api.delete(`/contacts/${contactId}/tags/${tagId}`);
      await load();
    } catch {
      setError("Failed to remove tag");
    }
  }

  async function setField(name: string, raw: string) {
    try {
      await api.patch(`/contacts/${contactId}`, {
        customFields: { [name]: raw },
      });
      await load();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(msg ?? "Failed to update field");
    }
  }

  async function toggleOptIn(key: "marketing" | "transactional") {
    if (!data) return;
    try {
      await api.patch(`/contacts/${contactId}`, {
        optIn: { [key]: !data.contact.optIn[key] },
      });
      await load();
    } catch {
      setError("Failed to update opt-in");
    }
  }

  if (loading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (error || !data) {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: "var(--error)" }}>{error ?? "Not found"}</p>
        <button onClick={() => void navigate({ to: "/contacts" })} className="brut-button">
          Back to contacts
        </button>
      </div>
    );
  }

  const c = data.contact;

  return (
    <section style={{ maxWidth: 920, margin: "0 auto" }}>
      <div style={{ marginBottom: 12 }}>
        <Link to="/contacts" className="brut-link-btn">← Contacts</Link>
      </div>
      <h1 style={{ margin: "0 0 4px", fontSize: 28 }}>{c.name ?? c.igUsername ?? "Unknown"}</h1>
      {c.igUsername ? (
        <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 16 }}>@{c.igUsername}</p>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Tags + opt-in */}
        <div className="brut-card" style={{ padding: 16 }}>
          <h2 style={{ fontSize: 13, letterSpacing: "0.05em", textTransform: "uppercase", margin: "0 0 10px" }}>
            Tags
          </h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {data.tags.length === 0 ? (
              <span style={{ color: "var(--muted)", fontSize: 13 }}>No tags yet.</span>
            ) : (
              data.tags.map((t) => {
                const meta = tagById.get(t.tagId);
                if (!meta) return null;
                return (
                  <span
                    key={t.tagId}
                    style={{
                      background: meta.color ?? "#eee",
                      border: "2px solid var(--ink)",
                      padding: "2px 6px",
                      fontSize: 12,
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {meta.name}
                    <button
                      onClick={() => void removeTag(t.tagId)}
                      style={{
                        border: 0,
                        background: "transparent",
                        cursor: "pointer",
                        fontSize: 14,
                        lineHeight: 1,
                        padding: 0,
                      }}
                      title="Remove tag"
                    >
                      ×
                    </button>
                  </span>
                );
              })
            )}
          </div>
          {availableTags.length > 0 ? (
            <select
              value={adding}
              onChange={(e) => {
                const v = e.target.value;
                if (v) void addTag(v);
              }}
              className="brut-input"
              style={{ fontSize: 13 }}
            >
              <option value="">+ Add tag…</option>
              {availableTags.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          ) : (
            <p style={{ fontSize: 12, color: "var(--muted)", margin: 0 }}>
              All defined tags applied. <Link to="/settings/tags">Create more →</Link>
            </p>
          )}

          <h2 style={{ fontSize: 13, letterSpacing: "0.05em", textTransform: "uppercase", margin: "16px 0 10px" }}>
            Opt-in
          </h2>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <input
              type="checkbox"
              checked={c.optIn.marketing}
              onChange={() => void toggleOptIn("marketing")}
            />
            <span>Marketing</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={c.optIn.transactional}
              onChange={() => void toggleOptIn("transactional")}
            />
            <span>Transactional</span>
          </label>
        </div>

        {/* Custom fields */}
        <div className="brut-card" style={{ padding: 16 }}>
          <h2 style={{ fontSize: 13, letterSpacing: "0.05em", textTransform: "uppercase", margin: "0 0 10px" }}>
            Custom fields
          </h2>
          {fieldDefs.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--muted)", margin: 0 }}>
              No fields defined yet. <Link to="/settings/custom-fields">Create some →</Link>
            </p>
          ) : (
            fieldDefs.map((def) => (
              <FieldEditor
                key={def.id}
                def={def}
                value={c.customFields[def.name]}
                onSave={(raw) => void setField(def.name, raw)}
              />
            ))
          )}
        </div>
      </div>

      <SubscriptionsPanel
        contactId={data.contact.id}
        subscriptions={data.subscriptions}
        onChange={() => void load()}
      />

      <div className="brut-card" style={{ marginTop: 16, padding: 16 }}>
        <h2 style={{ fontSize: 13, letterSpacing: "0.05em", textTransform: "uppercase", margin: "0 0 10px" }}>
          Recent messages
        </h2>
        {data.recentMessages.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--muted)", margin: 0 }}>No messages.</p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
            {data.recentMessages.slice(0, 20).map((m) => (
              <li
                key={m.id}
                style={{
                  padding: "6px 10px",
                  borderLeft: `3px solid ${m.direction === "inbound" ? "#7ac9a0" : "var(--accent)"}`,
                  background: m.direction === "inbound" ? "#f0faf5" : "#fffaeb",
                  fontSize: 13,
                }}
              >
                <span style={{ color: "var(--muted)", marginRight: 6, fontSize: 11 }}>
                  {new Date(m.createdAt).toLocaleString()}
                </span>
                <strong style={{ marginRight: 6 }}>
                  {m.direction === "inbound" ? "←" : "→"}
                </strong>
                {m.body?.text ?? `[${m.type}]`}
              </li>
            ))}
          </ul>
        )}
        {data.conversation ? (
          <div style={{ marginTop: 10 }}>
            <Link to="/inbox/$conversationId" params={{ conversationId: data.conversation.id }}>
              Open in inbox →
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function SubscriptionsPanel({
  contactId,
  subscriptions,
  onChange,
}: {
  contactId: string;
  subscriptions: ContactDetail["subscriptions"];
  onChange: () => void;
}) {
  const active = subscriptions.filter((s) => s.status === "active");
  const past = subscriptions.filter((s) => s.status !== "active");

  async function unsubscribe(sequenceId: string) {
    if (!confirm("Unsubscribe this contact from the sequence?")) return;
    try {
      await api.post(`/contacts/${contactId}/sequences/${sequenceId}/unsubscribe`);
      onChange();
    } catch {
      // eslint-disable-next-line no-alert
      alert("Failed to unsubscribe.");
    }
  }

  if (subscriptions.length === 0) return null;

  return (
    <div className="brut-card" style={{ marginTop: 16, padding: 16 }}>
      <h2 style={{ fontSize: 13, letterSpacing: "0.05em", textTransform: "uppercase", margin: "0 0 10px" }}>
        Sequence subscriptions
      </h2>
      {active.length > 0 ? (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
          {active.map((s) => (
            <li
              key={s.sequenceId}
              style={{
                padding: "8px 10px",
                background: "#f0faf5",
                border: "2px solid var(--ink)",
                fontSize: 13,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div>
                <strong>{s.sequenceName}</strong>
                <div style={{ color: "var(--muted)", fontSize: 12 }}>
                  At step {s.currentStepIdx + 1}
                  {s.nextRunAt
                    ? ` · next ${new Date(s.nextRunAt).toLocaleString()}`
                    : ""}
                </div>
              </div>
              <button
                onClick={() => void unsubscribe(s.sequenceId)}
                className="brut-button is-secondary"
                style={{ fontSize: 12, padding: "4px 8px" }}
              >
                Unsubscribe
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {past.length > 0 ? (
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--muted)" }}>
            {past.length} past subscription{past.length === 1 ? "" : "s"}
          </summary>
          <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0", display: "grid", gap: 4 }}>
            {past.map((s) => (
              <li key={s.sequenceId} style={{ fontSize: 12, color: "var(--muted)" }}>
                {s.sequenceName} · {s.status}
                {s.unsubscribedReason ? ` (${s.unsubscribedReason})` : ""}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function FieldEditor({
  def,
  value,
  onSave,
}: {
  def: FieldDef;
  value: unknown;
  onSave: (raw: string) => void;
}) {
  const [draft, setDraft] = useState<string>(stringifyValue(value));
  useEffect(() => {
    setDraft(stringifyValue(value));
  }, [value]);
  const dirty = draft !== stringifyValue(value);

  return (
    <div style={{ marginBottom: 10 }}>
      <label className="brut-label" style={{ fontSize: 12 }}>
        {def.label} <span style={{ color: "var(--muted)" }}>({def.type})</span>
      </label>
      <div style={{ display: "flex", gap: 6 }}>
        {def.type === "bool" ? (
          <select value={draft} onChange={(e) => setDraft(e.target.value)} className="brut-input">
            <option value="">—</option>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : (
          <input
            type={def.type === "number" ? "number" : def.type === "date" ? "date" : "text"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="brut-input"
            style={{ flex: 1 }}
          />
        )}
        {dirty ? (
          <button
            onClick={() => onSave(draft)}
            className="brut-button"
            style={{ fontSize: 12, padding: "6px 10px" }}
          >
            Save
          </button>
        ) : null}
      </div>
    </div>
  );
}

function stringifyValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
